import { useEffect, useState } from 'react';
import { ErrorState } from '../../../../components/ErrorState';
import type { MenuItem } from '../../../../components/Menu';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { Skeleton } from '../../../../components/Skeleton';
import { ApiError } from '../../../../lib/apiError';
import { surveyQuestionnairesApi } from '../surveyApi';
import type { SurveyQuestionnaire, SurveyQuestionnaireQuestion } from '../surveyTypes';
import { SurveyQuestionnairePreview, type PreviewQuestion } from './SurveyQuestionnairePreview';

function toPreview(q: SurveyQuestionnaireQuestion): PreviewQuestion {
  return {
    key: q.id,
    text: q.text,
    description: q.description ?? '',
    type: q.type,
    isRequired: q.isRequired,
    scaleMin: q.scaleMin,
    scaleMax: q.scaleMax,
    scaleMinLabel: q.scaleMinLabel ?? '',
    scaleMaxLabel: q.scaleMaxLabel ?? '',
    options: q.options.map((o) => ({ key: o.id, text: o.text })),
  };
}

/**
 * Configuration › Questionnaire when the survey HAS its one questionnaire — the questions on
 * arrival, drawn as a respondent sees them (product call, 09 Oct 2026), rather than a table of
 * one row to click through. Edit, Answer, the survey's Start / End and Delete sit behind the card's cog.
 */
export function SurveyQuestionnaireView({
  surveyId,
  questionnaire,
  version,
  deletedSurvey,
  onEdit,
  onAnswer,
  onDelete,
  statusItems,
}: {
  surveyId: string;
  questionnaire: SurveyQuestionnaire;
  /** Bumped by the panel after a write, so the questions are read again. */
  version: number;
  deletedSurvey: string | null;
  onEdit: () => void;
  onAnswer: () => void;
  onDelete: () => void;
  /** The survey's own start / end entry, drawn between Answer and Delete. */
  statusItems: MenuItem[];
}) {
  const [questions, setQuestions] = useState<SurveyQuestionnaireQuestion[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    surveyQuestionnairesApi
      .get(surveyId, questionnaire.id, controller.signal)
      .then((detail) => {
        if (controller.signal.aborted) return;

        setQuestions(detail.questions);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setError(caught instanceof ApiError ? caught : new ApiError('Could not load the questions.', 0));
      });

    return () => controller.abort();
  }, [surveyId, questionnaire.id, version]);

  const menuItems: MenuItem[] = [
    // The read key: anybody who can read the survey may open the form; answering is checked inside.
    {
      key: 'answer',
      label: 'Answer',
      permission: 'surveys.read',
      onClick: onAnswer,
    },
    ...statusItems,
    {
      key: 'delete',
      label: 'Delete',
      destructive: true,
      permission: 'surveys.update',
      disabledReason: deletedSurvey,
      onClick: onDelete,
    },
  ];

  return (
    <div className="modern-scrollbar h-full overflow-y-auto overflow-x-hidden p-4">
      {/* Half the panel's width and centred (product call, 09 Oct 2026 — the Settings width was tried and reverted the same day); full width below `lg`. */}
      <div className="mx-auto flex w-full flex-col gap-4 lg:w-1/2">
        <ProfileCard
          id="survey-profile-questionnaire"
          title="Questionnaire"
          count={questions?.length}
          onEdit={onEdit}
          editPermission="surveys.update"
          editDisabledReason={deletedSurvey}
          menuItems={menuItems}
          padded
        >
          {error && !questions ? (
            <ErrorState message={error.message} correlationId={error.correlationId} />
          ) : !questions ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <SurveyQuestionnairePreview
              heading={null}
              title={questionnaire.title}
              description={questionnaire.description ?? ''}
              questions={questions.map(toPreview)}
            />
          )}
        </ProfileCard>
      </div>
    </div>
  );
}
